import { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    InputAdornment,
    styled,
    Box, Snackbar, Alert,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import qs from 'qs';
import SetDefinition = Models.SetsPlayground.SetDefinition;

export interface GalleryUrlCardProps {
    galleryUrl: string;
    onUrlChange: (url: string) => void;
    setConfig: (config: Models.SetsPlayground.SetsConfig) => void;
}

const CardContainer = styled(Card)(({ theme }) => ({
    width: '100%',
    borderRadius: theme.spacing(2),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root.Mui-focused': {
        backgroundColor: theme.palette.custom.lightBlue,
    },
}));

const tryParseUrl = (value: string): URL | null => {
    try {
        return new URL(value);
    } catch {
        return null;
    }
};

const isTraditionalGallery = (u: URL): boolean => u.hostname.includes('gallery.vistaprint');

export const GalleryUrlCard = ({ galleryUrl, onUrlChange, setConfig }: GalleryUrlCardProps): JSX.Element => {
    const [error, setError] = useState<string | null>(null);
    const [successSnackBarOpen, setSuccessSnackBarOpen] = useState(false);

    const handleSnackBarClose = (): void => {
        setSuccessSnackBarOpen(false);
    };

    const validateUrl = (url: string): { isValid: boolean; parsed: URL | null; errorMessage?: string } => {
        if (url === '') {
            return { isValid: true, parsed: null };
        }

        const parsed = tryParseUrl(url);

        if (!parsed) {
            return { isValid: false, parsed: null, errorMessage: 'Enter a valid UBIK Gallery URL.' };
        }

        if (isTraditionalGallery(parsed)) {
            return {
                isValid: false,
                parsed,
                errorMessage: 'This is a traditional gallery URL (gallery.vistaprint.), enter a valid UBIK Gallery URL',
            };
        }

        return { isValid: true, parsed };
    };

    const processRawSetsFromUrl = (url: URL): void => {
        const queryParams = qs.parse(url.search, { ignoreQueryPrefix: true });

        if (queryParams.rawSets) {
            try {
                const setDefinitions: SetDefinition[] = JSON.parse(decodeURIComponent(queryParams.rawSets as string));

                setConfig({ sets: setDefinitions });
                setSuccessSnackBarOpen(true);
            } catch (err) {
                setError('Invalid rawSets parameter in the URL.');
            }
        }
    };

    const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;

        onUrlChange(next);

        const validation = validateUrl(next);

        if (!validation.isValid) {
            setError(validation.errorMessage || null);
            return;
        }

        setError(null);
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
        const pastedText = e.clipboardData.getData('text');

        const validation = validateUrl(pastedText);

        if (!validation.isValid || !validation.parsed) {
            return;
        }

        processRawSetsFromUrl(validation.parsed);
    };

    return (
        <>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={6000}
                open={successSnackBarOpen}
                onClose={handleSnackBarClose}
            >
                <Alert severity="success" sx={{ width: '100%' }} onClose={handleSnackBarClose}>
                    Sets configuration loaded from gallery link.
                </Alert>
            </Snackbar>
            <CardContainer>
                <CardContent>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Typography color="text.secondary" variant="body2">
                            Enter the gallery URL where you want to test your set configurations.
                            The tool will append the necessary parameters to create a testable link.
                        </Typography>
                        <StyledTextField
                            fullWidth
                            error={!!error}
                            helperText={error}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <LinkIcon color="action" />
                                    </InputAdornment>
                                ),
                            }}
                            label="Gallery Link"
                            placeholder="https://www.vistaprint.com/business-cards/standard/templates"
                            size="small"
                            value={galleryUrl}
                            onChange={handleUrlChange}
                            onPaste={handlePaste}
                        />
                    </Box>
                </CardContent>
            </CardContainer>
        </>
    );
};
